// Legacy-сборка pdf.js — та же, что в просмотре (components/pdf-view.tsx).
import workerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';

/**
 * Печать документа из просмотра. Фреймы и новые окна запрещены политикой безопасности (CSP),
 * а во фрейме PDF не печатают телефоны, поэтому страницы рисуются в картинки и на время печати
 * добавляются в саму страницу: стиль печати показывает только их.
 */

/** Разрешение печати: 150 точек на дюйм (в PDF 72 точки на дюйм). */
const PRINT_SCALE = 150 / 72;

async function printImages(urls: string[]): Promise<void> {
  const root = document.createElement('div');
  root.id = 'print-root';
  for (const url of urls) {
    const img = document.createElement('img');
    img.src = url;
    img.alt = '';
    root.appendChild(img);
  }
  // Поля страницы — у самого документа, свои поля принтера не добавляем.
  const page = document.createElement('style');
  page.textContent = '@page { margin: 0; }';
  document.head.appendChild(page);
  document.body.appendChild(root);
  document.body.classList.add('printing');
  await Promise.all([...root.querySelectorAll('img')].map((img) => img.decode().catch(() => undefined)));

  const cleanup = () => {
    window.removeEventListener('afterprint', cleanup);
    document.body.classList.remove('printing');
    root.remove();
    page.remove();
    urls.forEach((u) => u.startsWith('blob:') && URL.revokeObjectURL(u));
  };
  window.addEventListener('afterprint', cleanup);
  window.print();
  // Где afterprint не приходит (часть мобильных браузеров), убираем после закрытия диалога.
  setTimeout(() => document.body.classList.contains('printing') && cleanup(), 60_000);
}

/** PDF (в том числе PDF-копия Word/Excel): каждая страница — отдельный лист. */
export async function printPdf(url: string): Promise<void> {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error('Не удалось загрузить файл');
  const doc = await pdfjs.getDocument({ data: new Uint8Array(await res.arrayBuffer()) }).promise;
  const urls: string[] = [];
  try {
    for (let n = 1; n <= doc.numPages; n++) {
      const pg = await doc.getPage(n);
      const viewport = pg.getViewport({ scale: PRINT_SCALE });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      await pg.render({ canvas, viewport }).promise;
      const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/png'));
      if (!blob) throw new Error('Не удалось подготовить страницу к печати');
      urls.push(URL.createObjectURL(blob));
      pg.cleanup();
    }
  } finally {
    await doc.destroy();
  }
  await printImages(urls);
}

/** Картинка (скан в JPEG или PNG) — на один лист. */
export async function printImage(url: string): Promise<void> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error('Не удалось загрузить файл');
  await printImages([URL.createObjectURL(await res.blob())]);
}
